/**
 * Per-project accent colors. Each swatch is an OKLCH triple calibrated
 * for the dark theme (L ~0.68, C ~0.18). Stored verbatim in the
 * `projects.color` text column and read by ProjectView to set the
 * `--glow-color` CSS custom property — that's what drives the section
 * pulse + input focus glow inside that project.
 *
 * Picking the same hue family as the APETech accent (265, periwinkle)
 * as the default makes "no color chosen" identical to the global brand,
 * so the picker is fully opt-in.
 */
export interface ProjectColor {
  /** Stable id stored in DB. */
  id: string;
  /** Display label for the picker tooltip. */
  label: string;
  /** OKLCH string suitable for `--glow-color` and avatar tinting. */
  oklch: string;
}

export const PROJECT_COLORS: ProjectColor[] = [
  { id: 'electric', label: 'Electric blue', oklch: 'oklch(0.7 0.24 248)' },
  { id: 'periwinkle', label: 'Periwinkle', oklch: 'oklch(0.68 0.19 265)' },
  { id: 'blue', label: 'Blue', oklch: 'oklch(0.68 0.17 240)' },
  { id: 'cyan', label: 'Cyan', oklch: 'oklch(0.74 0.13 210)' },
  { id: 'teal', label: 'Teal', oklch: 'oklch(0.72 0.13 185)' },
  { id: 'emerald', label: 'Emerald', oklch: 'oklch(0.72 0.16 155)' },
  { id: 'lime', label: 'Lime', oklch: 'oklch(0.82 0.17 130)' },
  { id: 'amber', label: 'Amber', oklch: 'oklch(0.78 0.16 75)' },
  { id: 'orange', label: 'Orange', oklch: 'oklch(0.72 0.18 50)' },
  { id: 'rose', label: 'Rose', oklch: 'oklch(0.68 0.20 20)' },
  { id: 'magenta', label: 'Magenta', oklch: 'oklch(0.68 0.22 340)' },
  { id: 'violet', label: 'Violet', oklch: 'oklch(0.62 0.22 295)' },
  { id: 'slate', label: 'Slate', oklch: 'oklch(0.62 0.02 260)' },
];

export const DEFAULT_PROJECT_COLOR = PROJECT_COLORS[0]!;

/** Resolve any stored color value (id or raw oklch string) to an oklch
 *  string ready for CSS. Returns null if input is null/empty so callers
 *  can fall back to the global --glow-color default. */
export function resolveProjectColor(stored: string | null): string | null {
  if (!stored) return null;
  const swatch = PROJECT_COLORS.find((c) => c.id === stored);
  if (swatch) return swatch.oklch;
  // Stored value is not a known swatch id — assume it's already a raw
  // color string (legacy hex or custom oklch). Pass through.
  return stored;
}
